import { type JSONContent } from '@tiptap/react';
import { formatDuration, intervalToDuration, isMatch } from 'date-fns';
import { produce } from 'immer';
import {
  icons,
  MessageCircleDashedIcon,
  SearchIcon,
  SquarePenIcon
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import styles from './chatbot.module.css';
import ChatPanel from './ChatPanel';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

interface APIPresetPrompts {
  data: {
    category: string;
    created_at: string;
    icon: keyof typeof icons;
    id: number;
    is_active: boolean;
    prompt: string;
    should_show_in_navbar: boolean;
    updated_at: string;
    user_facing_title: string;
    user_message: string;
    variables: (
      | {
          default_value: null | string;
          label: string;
          name: string;
          options: null;
          placeholder: string;
          required: boolean;
          type: 'date';
        }
      | {
          default_value: null | string;
          label: string;
          name: string;
          options: null;
          placeholder: string;
          required: boolean;
          type: 'text';
        }
      | {
          default_value: null | string;
          label: string;
          name: string;
          options: string[];
          placeholder: string;
          required: boolean;
          type: 'combo-box';
        }
      | {
          default_value: null | string;
          label: string;
          name: string;
          options: string[];
          placeholder: string;
          required: boolean;
          type: 'select';
        }
    )[];
  }[];
  error: null;
  message: string;
  success: true;
  total: number;
}

type MessageData =
  | {
      connection_id: string;
      sessions_data: {
        active_connections: unknown[];
        available_sort_options: {
          fields: string[];
          orders: string[];
        };
        connection_id: string;
        connection_timestamp: number;
        session_count: number;
        session_summaries: unknown[];
        sessions: Session[];
        sort_order: string;
        sort_preferences: {
          sort_by: string;
          sort_order: string;
        };
        sorted_by: string;
        success: boolean;
        user_id: string;
      };
      timestamp: string;
      type: 'connected';
      user_id: string;
    }
  | {
      data: {
        applied_filters: {
          filter: string;
          type: string;
        }[];
        icon: string;
        message: string;
        session_id: string;
        workflow_type: string;
      };
      timestamp: string;
      type: 'progress';
      update_type: 'applied_filters';
    }
  | {
      data: {
        data: Record<string, unknown>;
        event_data: {
          data: Record<string, unknown>;
          icon: string;
          message: string;
          reasoning?: string;
          step: string;
        };
        event_name: string;
        event_type: string;
        icon: string;
        message: string;
        metadata: {
          checkpoint_ns: string;
          langgraph_checkpoint_ns: string;
          langgraph_node: string;
          langgraph_path: string[];
          langgraph_step: number;
          langgraph_triggers: string[];
          thread_id: string;
          user_id: string;
        };
        progress: number;
        reasoning: null | string;
        session_id: string;
        step: string;
        tags: string[];
        workflow_type: string;
      };
      timestamp: string;
      type: 'progress';
      update_type:
        | 'custom_data_combination_fetching_completed'
        | 'custom_data_data_preparation_completed'
        | 'custom_data_report_url_extraction_completed'
        | 'custom_formatting_response'
        | 'custom_ner_entity_extraction_completed'
        | 'custom_ner_final_assembly_completed'
        | 'custom_ner_object_search_completed'
        | 'custom_ner_result_formatting_completed'
        | 'custom_ner_temporal_context_extraction_completed'
        | 'custom_ner_user_confirmation_check_completed'
        | 'custom_ner_user_confirmation_processing'
        | 'custom_resolving_entities';
    }
  | {
      data: {
        icon: string;
        message: string;
        message_id: string;
        session_id: string;
        step: string;
        title: string;
        user_id: string;
      };
      timestamp: string;
      type: 'progress';
      update_type:
        | 'completed'
        | 'executing_agent'
        | 'execution_error'
        | 'formatting_response'
        | 'resolving_entities'
        | 'retrieving_data'
        | 'title_generated'
        | 'workflow_cancelled'
        | 'workflow_completed'
        | 'workflow_started'
        | 'workflow_stopped';
    }
  | {
      history_data: {
        message_count: number;
        messages: {
          chart_suggestions?: null | string;
          content: string;
          formatted_data?: null | string;
          message_id: string;
          message_order: number;
          metadata: {
            [key: string]: unknown;
            event_type?: string;
            message_type?: string;
            progress?: number;
            query_type?: string;
            step?: string;
            workflow_data: {
              applied_filters?: {
                filter: string;
                type: string;
              }[];
              data?: Record<string, unknown>;
              icon: string;
              message: string;
              message_id?: string;
              progress?: number;
              reasoning?: string;
              step?: string;
            };
            workflow_step?: string;
          };
          raw_data: null | string;
          role: 'assistant' | 'system' | 'user';
          timestamp: string;
          tool_call_id: null;
          tool_calls: null;
        }[];
        session_id: string;
        success: boolean;
      };
      session_id: string;
      timestamp: string;
      type: 'chat_history_response';
    }
  | {
      result: {
        cancelled?: true;
        formatted_result: PromptAndAnswer['answer'];
        message: string;
        message_id: string;
      };
      timestamp: string;
      type: 'query_completed';
    }
  | {
      result: {
        filter_confirmation_options: {
          filter_recommendations: (
            | {
                field_name: string;
                match_score: number;
                matched_value: null | string;
                options: {
                  column: string;
                  match_score: number;
                  search_term: string;
                  value: string;
                }[];
                reasoning: string;
                type: 'date';
              }
            | {
                field_name: string;
                matched_value: null | string;
                options: string[];
                type: 'select';
              }
            | {
                field_name: string;
                matched_values: null | string[];
                options: string[];
                type: 'multi-select';
              }
          )[];
          message: string;
          summary: string;
          type: 'filter_confirmation';
        };
        message_id: string;
      };
      timestamp: string;
      type: 'query_in_progress';
    }
  | {
      session_id: string;
      timestamp: string;
      type: 'query_started';
    };

const configSchema = z.object({
  VITE_ALLOWED_IFRAME_DOMAIN: z.url(),
  VITE_API_BASE_URL: z.url(),
  VITE_WS_URL: z.url()
});

const parsed = configSchema.safeParse({
  VITE_ALLOWED_IFRAME_DOMAIN: import.meta.env.VITE_ALLOWED_IFRAME_DOMAIN,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_WS_URL: import.meta.env.VITE_WS_URL
});

if (!parsed.success) {
  console.error(
    'Invalid environment configuration:',
    z.treeifyError(parsed.error)
  );

  throw new Error('Invalid environment configuration');
}

const Chatbot = () => {
  const { getJWT } = useAuth();

  const [businessEntities, setBusinessEntities] = useState<BusinessEntity[]>(
    []
  );
  const [isConnecting, setIsConnecting] = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const [isFetchingChatData, setIsFetchingChatData] = useState(false);
  const [promptsAndAnswers, setPromptsAndAnswers] = useState<PromptAndAnswer[]>(
    []
  );
  const [pending, setPending] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [userId, setUserId] = useState('');

  const handlePresetSelectRef = useRef<(label: string) => void>(null);
  const questionAnswerRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket>(null);
  const [socket, setSocket] = useState<null | WebSocket>(null);

  useEffect(() => {
    if (socketRef.current || businessEntities.length === 0) return;

    void (async () => {
      let userId: string;

      if (import.meta.env.DEV) {
        userId = 'b23461ed-b833-40c7-af8c-952e2ad06740';

        setUserId(userId);
      } else {
        // Try to get userId from URL parameter first (for backward compatibility)
        const userIdFromUrl = new URLSearchParams(window.location.search).get(
          'userId'
        );

        if (userIdFromUrl) {
          userId = userIdFromUrl;
          setUserId(userId);
        } else {
          // Fall back to authenticated userId
          const authData = await getJWT();

          if (!authData?.userId) {
            console.error(
              'userId parameter is required in production mode. Please provide userId in URL or ensure you are authenticated.'
            );
            setIsConnecting(false);
            return;
          }

          userId = authData.userId;
          setUserId(userId);
        }
      }

      const sessionId = uuidv4();
      setSessionId(sessionId);

      const ws = new WebSocket(import.meta.env.VITE_WS_URL);
      socketRef.current = ws;

      setSocket(ws);

      ws.addEventListener('open', () => {
        if (!socketRef.current) {
          throw new Error();
        }

        void (async () => {
          const authData = await getJWT();

          if (!authData) {
            setIsConnecting(false);
            return;
          }

          if (socketRef.current) {
            socketRef.current.send(
              JSON.stringify({
                data: {
                  access_token: authData.accessToken,
                  session_id: sessionId,
                  user_id: authData.userId
                },
                type: 'connect'
              })
            );
          }

          setIsConnecting(false);
        })();
      });

      ws.addEventListener('error', event => {
        console.error(event);
        setIsConnecting(false);
      });

      ws.addEventListener('message', event => {
        const data = JSON.parse(event.data as string) as MessageData;

        if (data.type === 'chat_history_response') {
          setIsFetchingChatData(false);

          data.history_data.messages.forEach(message => {
            const { content, message_id, metadata, role, timestamp } = message;

            if (role === 'user') {
              if (metadata.query_type === 'initial_query') {
                setPromptsAndAnswers(promptsAndAnswers => [
                  ...promptsAndAnswers,
                  {
                    answer: {
                      blocks: [
                        {
                          data: { paragraph: 'Stopped.' },
                          id: uuidv4(),
                          type: 'paragraph'
                        }
                      ],
                      id: uuidv4()
                    },
                    id: message_id,
                    prompt: {
                      content: [
                        {
                          content: content
                            .split(/{{(.*?)}}/g)
                            .reduce<
                              JSONContent[]
                            >((previousValue, currentValue, currentIndex) => {
                              if (currentIndex % 2 === 0) {
                                if (currentValue === '') return previousValue;

                                previousValue.push({
                                  text: currentValue,
                                  type: 'text'
                                });
                              } else {
                                const businessEntity = businessEntities.find(
                                  businessEntities => {
                                    return businessEntities.items.find(
                                      item =>
                                        item.name === currentValue ||
                                        (item.items &&
                                          item.name ===
                                            currentValue.split(': ')[0] &&
                                          item.items.find(
                                            item =>
                                              item.name ===
                                              currentValue.split(': ')[1]
                                          ))
                                    );
                                  }
                                );

                                if (!businessEntity) {
                                  if (isMatch(currentValue, 'yyyy-MM-dd')) {
                                    const timeFilters = businessEntities.find(
                                      businessEntity =>
                                        businessEntity.id === 'time_filters'
                                    );

                                    if (!timeFilters) {
                                      throw new Error(
                                        `"time_filters" business entity is missing.`
                                      );
                                    }

                                    previousValue.push({
                                      attrs: {
                                        color: timeFilters.color,
                                        iconName: timeFilters.iconName,
                                        id: timeFilters.id,
                                        text: currentValue
                                      },
                                      type: 'tag'
                                    });

                                    return previousValue;
                                  } else {
                                    previousValue.push({
                                      text: currentValue,
                                      type: 'text'
                                    });

                                    return previousValue;
                                  }
                                }

                                previousValue.push({
                                  attrs: {
                                    color: businessEntity.color,
                                    iconName: businessEntity.iconName,
                                    id: businessEntity.id,
                                    text: currentValue
                                  },
                                  type: 'tag'
                                });
                              }

                              return previousValue;
                            }, []),
                          type: 'paragraph'
                        }
                      ],
                      type: 'doc'
                    },
                    startTimestamp: timestamp,
                    tasks: [],
                    thinkingDuration: null
                  }
                ]);
              }
            } else if (role === 'assistant') {
              setPromptsAndAnswers(promptsAndAnswers =>
                produce(promptsAndAnswers, draft => {
                  draft[draft.length - 1].answer = JSON.parse(
                    content
                  ) as PromptAndAnswer['answer'];

                  draft[draft.length - 1].thinkingDuration = formatDuration(
                    intervalToDuration({
                      end: timestamp,
                      start: draft[draft.length - 1].startTimestamp
                    }),
                    {
                      delimiter: ' and ',
                      format: ['minutes', 'seconds']
                    }
                  );
                })
              );
            } else {
              const Icon =
                icons[
                  // eslint-disable-next-line import/namespace
                  metadata.workflow_data.icon.slice(0, -4) as keyof typeof icons
                ];

              if (metadata.workflow_data.applied_filters) {
                const {
                  workflow_data: { applied_filters }
                } = metadata;

                setPromptsAndAnswers(promptsAndAnswers => {
                  return produce(promptsAndAnswers, draft => {
                    draft[draft.length - 1].tasks.push({
                      data: {
                        appliedFilters: applied_filters,
                        icon: (
                          <Icon
                            className="mt-0.5 shrink-0"
                            size={16}
                          />
                        ),
                        title: metadata.workflow_data.message
                      },
                      id: uuidv4(),
                      type: 'applied-filters'
                    });
                  });
                });
              } else {
                setPromptsAndAnswers(promptsAndAnswers =>
                  produce(promptsAndAnswers, draft => {
                    draft[draft.length - 1].tasks.push({
                      data: {
                        description: metadata.workflow_data.reasoning,
                        icon: (
                          <Icon
                            className="mt-0.5 shrink-0"
                            size={16}
                          />
                        ),
                        title: metadata.workflow_data.message,
                        tooltip:
                          !metadata.workflow_data.data ||
                          Object.entries(metadata.workflow_data.data).length ===
                            0
                            ? undefined
                            : Object.entries(metadata.workflow_data.data).map(
                                ([k, v]) => `${k}: ${JSON.stringify(v)}`
                              )
                      },
                      id: uuidv4(),
                      type: 'default'
                    });
                  })
                );
              }
            }
          });
        } else if (data.type === 'connected') {
          setSessions(data.sessions_data.sessions);
        } else if (data.type === 'progress') {
          if (
            data.update_type === 'workflow_cancelled' ||
            data.update_type === 'workflow_completed' ||
            data.update_type === 'workflow_started' ||
            data.update_type === 'workflow_stopped'
          )
            return;

          if (data.update_type === 'title_generated') {
            setSessions(sessions => [
              {
                created_at: data.timestamp,
                is_active: true,
                metadata: {
                  query_type: 'assistant_query',
                  session_id: data.data.session_id,
                  workflow_type: 'default'
                },
                session_id: data.data.session_id,
                title: data.data.title,
                updated_at: data.timestamp
              },
              ...sessions
            ]);
          } else if (data.update_type === 'applied_filters') {
            const Icon =
              // eslint-disable-next-line import/namespace
              icons[data.data.icon.slice(0, -4) as keyof typeof icons];

            setPromptsAndAnswers(promptsAndAnswers => {
              return produce(promptsAndAnswers, draft => {
                draft[draft.length - 1].tasks.push({
                  data: {
                    appliedFilters: data.data.applied_filters,
                    icon: (
                      <Icon
                        className="mt-1.5 self-start"
                        size={16}
                      />
                    ),
                    title: data.data.message
                  },
                  id: uuidv4(),
                  type: 'applied-filters'
                });
              });
            });
          } else if (
            data.update_type === 'custom_data_combination_fetching_completed' ||
            data.update_type === 'custom_data_data_preparation_completed' ||
            data.update_type ===
              'custom_data_report_url_extraction_completed' ||
            data.update_type === 'custom_formatting_response' ||
            data.update_type === 'custom_ner_entity_extraction_completed' ||
            data.update_type === 'custom_ner_final_assembly_completed' ||
            data.update_type === 'custom_ner_object_search_completed' ||
            data.update_type === 'custom_ner_result_formatting_completed' ||
            data.update_type ===
              'custom_ner_temporal_context_extraction_completed' ||
            data.update_type ===
              'custom_ner_user_confirmation_check_completed' ||
            data.update_type === 'custom_ner_user_confirmation_processing' ||
            data.update_type === 'custom_resolving_entities'
          ) {
            const Icon =
              // eslint-disable-next-line import/namespace
              icons[data.data.icon.slice(0, -4) as keyof typeof icons];

            setPromptsAndAnswers(promptsAndAnswers => {
              return produce(promptsAndAnswers, draft => {
                draft[draft.length - 1].tasks.push({
                  data: {
                    description: data.data.reasoning ?? undefined,
                    icon: (
                      <Icon
                        className="mt-0.5 shrink-0"
                        size={16}
                      />
                    ),
                    title: data.data.message,
                    tooltip:
                      Object.entries(data.data.data).length === 0
                        ? undefined
                        : Object.entries(data.data.data).map(
                            ([k, v]) => `${k}: ${JSON.stringify(v)}`
                          )
                  },
                  id: uuidv4(),
                  type: 'default'
                });
              });
            });
          } else {
            const Icon =
              // eslint-disable-next-line import/namespace
              icons[data.data.icon.slice(0, -4) as keyof typeof icons];

            setPromptsAndAnswers(promptsAndAnswers => {
              return produce(promptsAndAnswers, draft => {
                draft[draft.length - 1].tasks.push({
                  data: {
                    icon: (
                      <Icon
                        className="mt-0.5 shrink-0"
                        size={16}
                      />
                    ),
                    title: data.data.message
                  },
                  id: uuidv4(),
                  type: 'default'
                });
              });
            });
          }

          if (
            window.scrollY + window.innerHeight >=
            document.body.scrollHeight - 100
          ) {
            setTimeout(() => {
              window.scrollTo({
                behavior: 'smooth',
                top: document.body.scrollHeight
              });
            }, 100);
          }
        } else if (data.type === 'query_completed') {
          const {
            result: { cancelled, formatted_result, message_id },
            timestamp
          } = data;

          if (!cancelled) {
            setPromptsAndAnswers(promptsAndAnswers =>
              produce(promptsAndAnswers, draft => {
                draft[draft.length - 1].answer = formatted_result;
                // Update the message ID with the backend's message_id from query_completed
                draft[draft.length - 1].id = message_id;

                draft[draft.length - 1].thinkingDuration = formatDuration(
                  intervalToDuration({
                    end: timestamp,
                    start: draft[draft.length - 1].startTimestamp
                  }),
                  {
                    delimiter: ' and ',
                    format: ['minutes', 'seconds']
                  }
                );
              })
            );
          }

          setPending(false);

          setTimeout(() => {
            questionAnswerRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          }, 100);
        } else if (data.type === 'query_in_progress') {
          setPromptsAndAnswers(promptsAndAnswers =>
            produce(promptsAndAnswers, draft => {
              draft[draft.length - 1].tasks.push({
                data: {
                  filters:
                    data.result.filter_confirmation_options.filter_recommendations.map(
                      filter => {
                        if (filter.type === 'date') {
                          return {
                            defaultValue:
                              filter.matched_value === '' ||
                              filter.matched_value === null
                                ? undefined
                                : isNaN(
                                      new Date(filter.matched_value).valueOf()
                                    )
                                  ? undefined
                                  : new Date(filter.matched_value),
                            isRequired: true,
                            label: filter.field_name,
                            name: filter.field_name,
                            type: 'date'
                          };
                        } else if (filter.type === 'multi-select') {
                          const businessEntity = businessEntities.find(
                            businessEntity =>
                              businessEntity.id === filter.options[0]
                          );

                          if (!businessEntity) {
                            throw new Error();
                          }

                          return {
                            defaultValues: filter.matched_values ?? undefined,
                            isRequired: true,
                            label: filter.field_name,
                            name: filter.field_name,
                            options: businessEntity.items.map(
                              item => item.name
                            ),
                            type: 'multi-select'
                          };
                        } else {
                          const businessEntity = businessEntities.find(
                            businessEntity =>
                              businessEntity.id === filter.options[0]
                          );

                          if (!businessEntity) {
                            throw new Error();
                          }

                          return {
                            defaultValue: filter.matched_value ?? undefined,
                            isRequired: true,
                            label: filter.field_name,
                            name: filter.field_name,
                            options: businessEntity.items.map(
                              item => item.name
                            ),
                            type: 'select'
                          };
                        }
                      }
                    ),
                  instructions: data.result.filter_confirmation_options.message
                },
                id: uuidv4(),
                type: 'input-fields'
              });
            })
          );

          setTimeout(() => {
            window.scrollTo({
              behavior: 'smooth',
              top: document.body.scrollHeight
            });
          }, 100);
        } else {
          const { timestamp } = data;

          setPromptsAndAnswers(promptsAndAnswers =>
            produce(promptsAndAnswers, draft => {
              draft[draft.length - 1].startTimestamp = timestamp;
            })
          );
        }
      });
    })();
  }, [businessEntities, getJWT]);

  const getChatHistory = useCallback(
    (targetSessionId: string) => {
      if (!socketRef.current) {
        throw new Error();
      }

      setIsFetchingChatData(true);

      void (async () => {
        const authData = await getJWT();

        if (!authData) {
          setIsFetchingChatData(false);
          return;
        }

        if (socketRef.current) {
          socketRef.current.send(
            JSON.stringify({
              data: {
                access_token: authData.accessToken,
                session_id: targetSessionId,
                user_id: authData.userId
              },
              type: 'get_chat_history'
            })
          );
        }
      })();
    },
    [getJWT]
  );

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/business-entities`,
          {
            credentials: 'include'
          }
        );

        if (!response.ok) {
          console.error(
            `Failed to fetch business entities: ${String(response.status)} ${response.statusText}`
          );
          setBusinessEntities([]);
          setIsFetching(false);
          return;
        }

        const entities = (await response.json()) as BusinessEntity[];

        setBusinessEntities(entities);

        setIsFetching(false);
      } catch (error) {
        console.error('Error fetching business entities:', error);
        setBusinessEntities([]);
        setIsFetching(false);
      }
    })();
  }, []);

  const handleNewChatButtonClick = useCallback(() => {
    setPromptsAndAnswers([]);
    setSessionId(uuidv4());
  }, [setSessionId]);

  const [searchValue, setSearchValue] = useState('');

  const [presetPrompts, setPresetPrompts] = useState<PresetPrompt[]>([]);

  useEffect(() => {
    void (async () => {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/preset-prompts`,
        {
          credentials: 'include'
        }
      );

      const presetPromptsData = (await response.json()) as APIPresetPrompts;

      setPresetPrompts(
        presetPromptsData.data.map(presetPrompt => {
          const Icon =
            presetPrompt.icon in icons
              ? // eslint-disable-next-line import/namespace
                icons[presetPrompt.icon]
              : null;

          return {
            badge: null,
            details: presetPrompt.prompt,
            icon: Icon ? <Icon size={16} /> : null,
            id: presetPrompt.id.toString(),
            label: presetPrompt.user_facing_title,
            prompt: presetPrompt.user_message,
            shouldShowInNavbar: presetPrompt.should_show_in_navbar,
            variables: presetPrompt.variables
          };
        })
      );
    })();
  }, []);

  const isLoading = isConnecting || isFetching;

  const filteredSessions = useMemo(
    () =>
      sessions.filter(session =>
        session.title.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [sessions, searchValue]
  );

  const filteredPresetPrompts = useMemo(
    () =>
      presetPrompts.filter(
        presetPrompt =>
          presetPrompt.shouldShowInNavbar &&
          presetPrompt.label.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [presetPrompts, searchValue]
  );

  useEffect(() => {
    if (isLoading) return;

    const inlineCss = document.getElementById('inline-css');
    if (inlineCss) {
      inlineCss.remove();
    }

    const preloader = document.getElementById('preloader');
    if (preloader) {
      preloader.remove();
    }
  }, [isLoading]);

  if (isLoading) return null;

  const currentSession = sessions.find(
    session => session.session_id === sessionId
  );

  return (
    <div className={cn(styles.chatbot, 'flex h-full w-full flex-col')}>
      {/* Full Width Header */}
      <header className="flex h-16 shrink-0 items-center gap-2 border-b bg-white px-4">
        <SidebarTrigger />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="size-8"
              disabled={pending}
              onClick={handleNewChatButtonClick}
              size="icon"
              variant="ghost"
            >
              <SquarePenIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>New Chat</p>
          </TooltipContent>
        </Tooltip>
        {promptsAndAnswers.length !== 0 &&
          (currentSession !== undefined ? (
            <div className="text-sm font-semibold">{currentSession.title}</div>
          ) : (
            <Skeleton className="h-5 w-[200px]" />
          ))}
      </header>

      {/* Content Area with Sidebar and Chat */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Chat History */}
        <div className="bg-sidebar flex h-full w-64 flex-col border-r">
          {/* Search */}
          <div className="border-b p-4">
            <div className="relative w-full">
              <SearchIcon
                className="text-muted-foreground absolute top-0 bottom-0 left-3 my-auto"
                size={16}
              />
              <Input
                className="bg-white pl-8"
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
                placeholder="Search"
                type="search"
                value={searchValue}
              />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col gap-6 p-4">
              {/* Presets Section */}
              {filteredPresetPrompts.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-muted-foreground px-2 text-sm font-medium">
                    Presets
                  </h3>
                  <div className="space-y-1">
                    {filteredPresetPrompts.map(presetPrompt => (
                      <button
                        className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={pending}
                        key={presetPrompt.id}
                        onClick={() => {
                          if (!handlePresetSelectRef.current) return;
                          handlePresetSelectRef.current(presetPrompt.label);
                        }}
                      >
                        {presetPrompt.icon}
                        <span className="truncate">{presetPrompt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat History Section */}
              <div className="space-y-2">
                <h3 className="text-muted-foreground px-2 text-sm font-medium">
                  Chat History
                </h3>
                {filteredSessions.length === 0 ? (
                  <Empty className="mt-2 border border-dashed p-6">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <MessageCircleDashedIcon />
                      </EmptyMedia>
                      <EmptyTitle>No Chats Yet</EmptyTitle>
                      <EmptyDescription>
                        You haven't started a chat yet. Send a query to get
                        started.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  <div className="space-y-1">
                    {filteredSessions.map(session => (
                      <button
                        className={cn(
                          'hover:bg-accent flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                          session.session_id === sessionId &&
                            'bg-accent font-medium'
                        )}
                        disabled={pending}
                        key={session.session_id}
                        onClick={() => {
                          getChatHistory(session.session_id);
                          setSessionId(session.session_id);
                        }}
                      >
                        <span className="truncate">{session.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Chat Interface */}
        <div className="flex flex-1 flex-col bg-white">
          {/* Chat Panel */}
          <ChatPanel
            businessEntities={businessEntities}
            handlePresetSelectRef={handlePresetSelectRef}
            isFetchingChatData={isFetchingChatData}
            pending={pending}
            presetPrompts={presetPrompts}
            promptsAndAnswers={promptsAndAnswers}
            ref={questionAnswerRef}
            sessionId={sessionId}
            setPending={setPending}
            setPromptsAndAnswers={setPromptsAndAnswers}
            socket={socket}
            userId={userId}
          />
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
